import React from "react";
import {Collapse, InputNumber, Select, Space, Switch, Tag, Typography} from "antd";
import Editor from "@monaco-editor/react";

const {Text} = Typography;

const RECOGNIZERS = [
    {key: "dynamicTagging", label: "Dynamic Tagging"},
    {key: "privacyFilter", label: "Privacy Filter"},
    {key: "patterns", label: "Pattern Recognizers"},
    {key: "trainedTagging", label: "Trained Tagging"},
    {key: "llmTagging", label: "LLM Tagging"}
];

const sameArray = (a, b) => JSON.stringify([...(a || [])].sort()) === JSON.stringify([...(b || [])].sort());

/**
 * Form editor for a detection profile DELTA. Shows the EFFECTIVE values
 * (default config as baseline); only actual deviations are written into the
 * delta - the "overridden" tags show what this profile changes. Everything
 * not covered by the form lives in the Advanced JSON section.
 */
const ProfileEditor = ({delta, defaultConfig, onChange}) => {
    const d = delta || {};
    const def = defaultConfig || {};

    const update = (next) => {
        // drop blocks that ended up empty so the delta stays minimal
        const cleaned = {};
        Object.keys(next).forEach((k) => {
            const v = next[k];
            if (v === undefined) return;
            if (v && typeof v === "object" && !Array.isArray(v) && Object.keys(v).length === 0) return;
            cleaned[k] = v;
        });
        onChange(cleaned);
    };

    const setBlockKey = (block, key, value, defaultValue) => {
        const blockDelta = {...(d[block] || {})};
        if (value === defaultValue || value === null) {
            delete blockDelta[key];
        } else {
            blockDelta[key] = value;
        }
        update({...d, [block]: Object.keys(blockDelta).length ? blockDelta : undefined});
    };

    // --- recognizer on/off --------------------------------------------------
    const recognizerEnabled = (key) => !(d[key] && d[key].enabled === false);
    const toggleRecognizer = (key, on) => {
        const blockDelta = {...(d[key] || {})};
        if (on) {
            delete blockDelta.enabled;
        } else {
            blockDelta.enabled = false;
        }
        update({...d, [key]: Object.keys(blockDelta).length ? blockDelta : undefined});
    };

    // --- entity type subset ---------------------------------------------------
    const allEntityTypes = Object.keys(def.entities || {});
    const selectedEntities = d.entities ? Object.keys(d.entities) : allEntityTypes;
    const changeEntities = (selection) => {
        if (sameArray(selection, allEntityTypes)) {
            update({...d, entities: undefined});
            return;
        }
        const subset = {};
        selection.forEach((t) => {
            subset[t] = (d.entities && d.entities[t]) || (def.entities || {})[t] || {labels: []};
        });
        update({...d, entities: subset});
    };

    // --- thresholds -----------------------------------------------------------
    const defGlinerTh = def.dynamicTagging ? def.dynamicTagging.threshold : 0.6;
    const defPfTh = def.privacyFilter ? def.privacyFilter.threshold : 0.5;
    const glinerTh = d.dynamicTagging && d.dynamicTagging.threshold !== undefined ? d.dynamicTagging.threshold : defGlinerTh;
    const pfTh = d.privacyFilter && d.privacyFilter.threshold !== undefined ? d.privacyFilter.threshold : defPfTh;

    // --- ignore list ------------------------------------------------------------
    const defIgnore = def.ignoreValues || [];
    const ignoreValues = d.ignoreValues !== undefined ? d.ignoreValues : defIgnore;
    const changeIgnore = (values) => {
        update({...d, ignoreValues: sameArray(values, defIgnore) ? undefined : values});
    };

    const overridden = (cond) => cond && <Tag color="purple" style={{marginLeft: 6}}>overridden</Tag>;

    return (
        <div>
            <div style={{marginBottom: 14}}>
                <Text strong style={{fontSize: 13}}>Recognizers</Text>
                {overridden(RECOGNIZERS.some((r) => d[r.key] && d[r.key].enabled === false))}
                <div style={{display: "flex", gap: 20, marginTop: 6, flexWrap: "wrap"}}>
                    {RECOGNIZERS.map((r) => (
                        <span key={r.key}>
                            <Switch
                                size="small"
                                checked={recognizerEnabled(r.key)}
                                onChange={(on) => toggleRecognizer(r.key, on)}
                                style={{marginRight: 6}}
                            />
                            <Text style={{fontSize: 13}} type={recognizerEnabled(r.key) ? undefined : "secondary"}>{r.label}</Text>
                        </span>
                    ))}
                </div>
            </div>

            <div style={{marginBottom: 14}}>
                <Text strong style={{fontSize: 13}}>Detected entity types</Text>
                {overridden(!!d.entities)}
                <Select
                    mode="multiple"
                    style={{width: "100%", marginTop: 6}}
                    value={selectedEntities}
                    onChange={changeEntities}
                    options={allEntityTypes.map((t) => ({value: t, label: t}))}
                    placeholder="All entity types"
                />
                <Text type="secondary" style={{fontSize: 12}}>
                    Deselect types this profile should NOT detect (e.g. remove PERSON for internal worklogs).
                </Text>
            </div>

            <Space size="large" style={{marginBottom: 14}}>
                <span>
                    <Text strong style={{fontSize: 13}}>Dynamic Tagging threshold</Text>
                    {overridden(!!(d.dynamicTagging && d.dynamicTagging.threshold !== undefined))}
                    <div>
                        <InputNumber
                            min={0} max={1} step={0.05}
                            value={glinerTh}
                            onChange={(v) => setBlockKey("dynamicTagging", "threshold", v, defGlinerTh)}
                            style={{width: 120, marginTop: 4}}
                        />
                        <Text type="secondary" style={{fontSize: 12, marginLeft: 8}}>default {defGlinerTh}</Text>
                    </div>
                </span>
                <span>
                    <Text strong style={{fontSize: 13}}>Privacy Filter threshold</Text>
                    {overridden(!!(d.privacyFilter && d.privacyFilter.threshold !== undefined))}
                    <div>
                        <InputNumber
                            min={0} max={1} step={0.05}
                            value={pfTh}
                            onChange={(v) => setBlockKey("privacyFilter", "threshold", v, defPfTh)}
                            style={{width: 120, marginTop: 4}}
                        />
                        <Text type="secondary" style={{fontSize: 12, marginLeft: 8}}>default {defPfTh}</Text>
                    </div>
                </span>
            </Space>

            <div style={{marginBottom: 14}}>
                <Text strong style={{fontSize: 13}}>Ignore list</Text>
                {overridden(d.ignoreValues !== undefined)}
                <Select
                    mode="tags"
                    style={{width: "100%", marginTop: 6}}
                    value={ignoreValues}
                    onChange={changeIgnore}
                    tokenSeparators={[","]}
                    placeholder="Values never reported as findings"
                />
            </div>

            <Collapse ghost>
                <Collapse.Panel header={<Text type="secondary" style={{fontSize: 13}}>Advanced (raw delta JSON)</Text>} key="json">
                    <div style={{border: "1px solid #f0f0f0", borderRadius: 6}}>
                        <Editor
                            height="200px"
                            defaultLanguage="json"
                            value={JSON.stringify(d, null, 2)}
                            onChange={(v) => {
                                try {
                                    onChange(JSON.parse(v || "{}"));
                                } catch (e) {
                                    // ignore while typing
                                }
                            }}
                            options={{minimap: {enabled: false}}}
                        />
                    </div>
                </Collapse.Panel>
            </Collapse>
        </div>
    );
};

export default ProfileEditor;
